import { TableWrapperStack } from '@common/components';
import { theme } from '@prophecy/theme';
import { Controller, Select, Stack, Text } from '@prophecy/ui-v3';
import React from 'react';
import { UseFormGetValues } from 'react-hook-form';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { AuthTypes } from '../../../../data/apis/types';
import { AuthProviderOptions } from '../../config/admin';
import { StyledHeading } from '../../config/styled';
import AzureAuth from './AzureAuth';
import GoogleAuth from './GoogleAuth';
import LdapAuth from './LdapAuth';
import ProphecyManaged from './ProphecyManaged';
import SamlAuth from './SamlAuth';
import { AdminData, ComponentProps } from './types';

const authComponentMap: { [key: string]: (props: ComponentProps) => JSX.Element | null } = {
  ProphecyManaged: ProphecyManaged,
  ldap: LdapAuth,
  saml: SamlAuth,
  azuread: AzureAuth,
  google: GoogleAuth
};
type Props = ComponentProps & {
  getValues: UseFormGetValues<AdminData>;
  watch: UseFormGetValues<AdminData>;
  isSaasWithSCIM?: boolean;
  isAzureSCIMEnabled?: boolean;
};

export default function AuthProvider(props: Props) {
  const globalConf = useGlobalVariables();
  const cloudProvider = globalConf.getCloudProvider as string;

  const { control, errors, watch, isOktaConfigured, isSaasWithSCIM } = props;
  const getSelectedComponent = (authProvider: string) => {
    const AuthComponent = authComponentMap[authProvider];
    return (
      <AuthComponent
        {...props}
        IsUsingManagedIdentity={watch('azuread.IsUsingManagedIdentity')}
        userMatchers={watch('ldap.groupSearch.userMatchers')}
      />
    );
  };

  const authProvider = watch('authProvider');
  let authOptions = AuthProviderOptions;

  if (cloudProvider?.toLowerCase() === 'saas') {
    authOptions = authOptions.filter(
      (option) => option.value === AuthTypes.prophecymanaged || option.value === AuthTypes.saml
    );
  }

  return (
    <TableWrapperStack gap={theme.spaces.x24} height='100%'>
      <Stack gap={theme.spaces.x12}>
        <StyledHeading level='xl'>Authentication Provider</StyledHeading>
        <Text level='xs'>
          Allows you to setup your organization’s authentication provider of choice. Changing the authentication
          provider will not affect the existing user’s profiles.
        </Text>
      </Stack>

      <Stack padding={`0 ${theme.spaces.x8} 0 ${theme.spaces.x4}`}>
        <Controller
          render={({ field }) => (
            <Select
              label='Authentication Provider'
              {...field}
              disabled={isSaasWithSCIM && !isOktaConfigured}
              showSearch={true}
              options={authOptions}
              data-test-id='admin-auth-provider-select'
              variant={errors.authProvider ? 'error' : 'primary'}
              helpText={errors.authProvider?.message}
            />
          )}
          control={control}
          name='authProvider'
          rules={{
            required: 'Auth Provider is required'
          }}
        />
      </Stack>

      {getSelectedComponent(authProvider)}
    </TableWrapperStack>
  );
}
